import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  inject,
} from '@angular/core';
import { AnyAbility } from '@casl/ability';
import { AblePurePipe } from '@casl/angular';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import {
  defaultColDef,
  defaultGridOptionsFactory,
} from '@wilson/ag-grid/configuration';
import {
  ActivityStatusRendererComponent,
  ClientAgreementRendererComponent,
  RedirectingServiceLinkRendererComponent,
  RedirectingShiftLinkRendererComponent,
  WilsonShareJobLinkRendererComponent,
} from '@wilson/ag-grid/renderers';
import { GridService } from '@wilson/ag-grid/service';
import { ShiftAttachmentsService } from '@wilson/api/gateway';
import { FeaturePurePipe } from '@wilson/authorization';
import { FeatureFlagPurePipe } from '@wilson/feature-flags';
import {
  FeatureName,
  RoleAction,
  RolePermissionSubject,
  ShiftAttachment,
} from '@wilson/interfaces';
import { InvoicingActivitySelectionService } from '@wilson/invoicing/services';
import {
  ActivityViewModel,
  InvoicingDataState,
  InvoicingFetchShifts,
  InvoicingSetPageSize,
  InvoicingSetPagination,
} from '@wilson/invoicing/state';
import { LocalizedDateFormatPipe } from '@wilson/localization';
import {
  CustomInfiniteRowModelSelectAllHeaderComponent,
  SELECT_ALL_STORE,
} from '@wilson/non-domain-specific/ag-grid-custom/ag-grid-select-all';
import {
  ShiftAttachmentReportsRendererComponent,
  ShiftAttachmentReportsRendererParams,
} from '@wilson/non-domain-specific/shift-helpers/core';
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IDatasource,
  IGetRowsParams,
  PaginationChangedEvent,
  RowClassParams,
  SortModelItem,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { isValid } from 'date-fns';
import { cloneDeep } from 'lodash';
import {
  Observable,
  Subject,
  combineLatest,
  debounceTime,
  firstValueFrom,
  map,
  of,
  shareReplay,
  skip,
  takeUntil,
  tap,
} from 'rxjs';
import { ActivityShiftLabelRendererComponent } from '../../renderers/activity-shift-label-renderer.component';
import { ActivityUserLabelRendererComponent } from '../../renderers/activity-user-label-renderer.component';

@Component({
  selector: 'wilson-invoicing-list-element',
  templateUrl: './invoicing-list-element.component.html',
  styleUrls: ['./invoicing-list-element.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    {
      provide: SELECT_ALL_STORE,
      useExisting: InvoicingActivitySelectionService,
    },
  ],
})
export class InvoicingListElementComponent implements OnDestroy {
  readonly gridService = inject(GridService);
  readonly store = inject(Store);
  private featureFlagPurePipe = inject(FeatureFlagPurePipe);
  private ablePurePipe = inject<AblePurePipe<AnyAbility>>(AblePurePipe);
  private featurePurePipe = inject(FeaturePurePipe);
  private readonly translateService = inject(TranslateService);
  private readonly shiftAttachmentsService = inject(ShiftAttachmentsService);
  private readonly localizationDateFormat = inject(LocalizedDateFormatPipe);

  paginationPageSize = this.store.selectSnapshot(
    InvoicingDataState.getPageSize,
  );
  pageSizes = this.store.selectSnapshot(InvoicingDataState.pageSizes);
  cacheBlockSize = this.store.selectSnapshot(InvoicingDataState.cacheBlockSize);

  public readonly defaultColDef = { ...defaultColDef, autoHeight: false };
  public readonly gridOptions: GridOptions = {
    ...defaultGridOptionsFactory(),
    paginationAutoPageSize: false,
    paginationPageSize: this.paginationPageSize,
    cacheBlockSize: this.cacheBlockSize,
    paginationPageSizeSelector: this.pageSizes,
    getRowStyle: (params: RowClassParams) => {
      if (params.data === undefined) {
        return { opacity: 0 };
      }
      return;
    },
    rowHeight: 55,
    getRowId: (params) => String(params.data.id),
    onPaginationChanged: (event: PaginationChangedEvent) => {
      if (event?.newPageSize) {
        this.store.dispatch([
          new InvoicingSetPageSize(event.api.paginationGetPageSize()),
          new InvoicingSetPagination(0),
        ]);
      }
    },
  };
  private destroy$ = new Subject<void>();
  private shiftAttachmentReports$: Observable<
    Record<string, ShiftAttachment[]>
  > = of({});

  private hasWilsonShareReadAccess$ = combineLatest([
    this.featureFlagPurePipe.transform('wilson-share-feature'),
    this.ablePurePipe.transform(
      RoleAction.Read,
      RolePermissionSubject.WilsonShare,
    ),
    this.featurePurePipe.transform(FeatureName.WilsonShare),
  ]).pipe(
    map(
      ([isEnabledOnLaunchDarkly, hasPermissionToRead, hasWilsonFeature]: [
        boolean,
        boolean,
        boolean,
      ]) => isEnabledOnLaunchDarkly && hasPermissionToRead && hasWilsonFeature,
    ),
    shareReplay(1),
  );

  private hasShiftAttachmentReportsReadAccess$ = combineLatest([
    this.featureFlagPurePipe.transform('approved-time-report-attachments'),
    this.ablePurePipe.transform(
      RoleAction.Read,
      RolePermissionSubject.ShiftAttachment,
    ),
  ]).pipe(
    map(
      ([isEnabledOnLaunchDarkly, hasPermissionToRead]: [boolean, boolean]) =>
        isEnabledOnLaunchDarkly && hasPermissionToRead,
    ),
    shareReplay(1),
  );

  protected readonly components = {
    selectAllHeaderComponent: CustomInfiniteRowModelSelectAllHeaderComponent,
    clientAgreementRendererComponent: ClientAgreementRendererComponent,
    shiftLinkRendererComponent: RedirectingShiftLinkRendererComponent,
    serviceLinkRendererComponent: RedirectingServiceLinkRendererComponent,
    activityStatusRendererComponent: ActivityStatusRendererComponent,
    wilsonShareJobLinkRendererComponent: WilsonShareJobLinkRendererComponent,
    activityUserLabelRendererComponent: ActivityUserLabelRendererComponent,
    activityShiftLabelRendererComponent: ActivityShiftLabelRendererComponent,
    shiftAttachmentReportsRendererComponent:
      ShiftAttachmentReportsRendererComponent,
  };

  protected columnDefs$: Observable<ColDef[]> = combineLatest([
    this.featureFlagPurePipe.transform('portal-labels'),
    this.ablePurePipe.transform(RoleAction.Read, RolePermissionSubject.Label),
    this.ablePurePipe.transform(RoleAction.Read, RolePermissionSubject.Client),
    this.ablePurePipe.transform(RoleAction.Read, RolePermissionSubject.Billing),
    this.hasWilsonShareReadAccess$,
    this.hasShiftAttachmentReportsReadAccess$,
  ]).pipe(
    map(
      ([
        isPortalLabelsEnabled,
        hasLabelReadPermission,
        hasClientReadPermission,
        hasBillingReadPermission,
        hasWilsonShareAccess,
        hasShiftAttachmentReportsReadAccess,
      ]) => [
        {
          resizable: false,
          sortable: false,
          width: 50,
          checkboxSelection: true,
          headerComponent: 'selectAllHeaderComponent',
          pinned: 'left',
          lockPinned: true,
          lockPosition: true,
        },
        {
          flex: 1,
          minWidth: 130,
          headerName: this.translateService.instant('general.staff'),
          field: 'user',
          sort: 'asc',
          pinned: 'left',
          lockPinned: true,
          lockPosition: true,
        },
        ...(hasLabelReadPermission && isPortalLabelsEnabled
          ? [
              {
                flex: 2,
                minWidth: 200,
                sortable: false,
                headerName: this.translateService.instant(
                  'page.employees.table.header.labels_employee',
                ),
                resizable: true,
                cellRenderer: 'activityUserLabelRendererComponent',
                field: 'shift.user.labels',
              },
            ]
          : []),
        {
          flex: 1,
          minWidth: 170,
          field: 'shiftDetails.shiftName',
          headerName: this.translateService.instant('page.shifts.shiftname'),
          cellRenderer: 'shiftLinkRendererComponent',
        },
        ...(hasShiftAttachmentReportsReadAccess
          ? [
              {
                flex: 1,
                minWidth: 175,
                sortable: false,
                field: 'shiftDetails',
                headerName: this.translateService.instant(
                  'page.working_times.monthly_view.header.shift_reports',
                ),
                cellRenderer: 'shiftAttachmentReportsRendererComponent',
                valueGetter: (
                  params: ValueGetterParams,
                ): ShiftAttachmentReportsRendererParams => {
                  return {
                    shiftAttachmentReports$: this.shiftAttachmentReports$.pipe(
                      map(
                        (shiftAttachmentReports) =>
                          shiftAttachmentReports[
                            params?.data?.shiftDetails?.shiftId
                          ] || [],
                      ),
                    ),
                    shiftName: null,
                    shiftId: params?.data?.shiftDetails?.shiftId || null,
                  };
                },
              },
            ]
          : []),
        {
          flex: 1,
          minWidth: 125,
          field: 'shiftDetails.shiftReportedStartDate',
          headerName: this.translateService.instant(
            'page.invoice_overview.shift_date',
          ),
          valueFormatter: this.shiftDateFormatter.bind(this),
        },
        ...(hasLabelReadPermission && isPortalLabelsEnabled
          ? [
              {
                flex: 2,
                minWidth: 150,
                sortable: false,
                headerName: this.translateService.instant(
                  'page.employees.table.header.labels_shift',
                ),
                resizable: true,
                cellRenderer: 'activityShiftLabelRendererComponent',
                field: 'shift.labels',
              },
            ]
          : []),
        {
          flex: 1,
          minWidth: 150,
          field: 'serviceDetails.serviceName',
          cellRenderer: 'serviceLinkRendererComponent',
          headerName: this.translateService.instant('page.shifts.service_no'),
        },
        {
          flex: 1,
          minWidth: 150,
          sortable: false,
          headerName: this.translateService.instant('general.activity'),
          field: 'activityName',
        },
        {
          flex: 1,
          field: 'invoices',
          minWidth: 100,
          sortable: false,
          headerName: this.translateService.instant('general.status'),
          cellRenderer: 'activityStatusRendererComponent',
        },
        {
          flex: 1,
          minWidth: 150,
          field: 'formattedStartDateTime',
          headerName: this.translateService.instant(
            'page.invoice_overview.service_date',
          ),
        },
        ...(hasClientReadPermission && hasBillingReadPermission
          ? [
              {
                width: 150,
                minWidth: 150,
                sortable: false,
                field: 'agreementDetails.clientName',
                headerName: this.translateService.instant('activity.agreement'),
                cellRenderer: 'clientAgreementRendererComponent',
              },
            ]
          : []),
        ...(hasWilsonShareAccess
          ? [
              {
                flex: 1,
                minWidth: 150,
                field: 'jobName',
                sortable: false,
                cellRenderer: 'wilsonShareJobLinkRendererComponent',
                headerName: this.translateService.instant(
                  'wilson.share.wilson_share_request',
                ),
              },
            ]
          : []),
      ],
    ),
  );

  private async getActivities({
    gridApi,
    startRow,
    endRow,
    hasWilsonShareReadAccess,
  }: {
    gridApi: GridApi;
    startRow: number;
    endRow: number;
    hasWilsonShareReadAccess: boolean;
    hasShiftAttachmentReportsReadAccess: boolean;
  }): Promise<void> {
    await firstValueFrom(
      this.store.dispatch([
        new InvoicingSetPagination(gridApi.paginationGetCurrentPage() + 1),
        new InvoicingFetchShifts(startRow, endRow, hasWilsonShareReadAccess),
      ]),
    );
  }

  async onGridReady(params: GridReadyEvent): Promise<void> {
    this.gridService.onGridReady(params);
    const gridApi = params.api;
    const hasWilsonShareReadAccess = await firstValueFrom(
      this.hasWilsonShareReadAccess$,
    );
    const hasShiftAttachmentReportsReadAccess = await firstValueFrom(
      this.hasShiftAttachmentReportsReadAccess$,
    );

    const dataSource: IDatasource = {
      getRows: async (params: IGetRowsParams) => {
        gridApi.setGridOption('loading', true);
        await this.getActivities({
          gridApi,
          startRow: params.startRow,
          endRow: params.endRow,
          hasWilsonShareReadAccess,
          hasShiftAttachmentReportsReadAccess,
        });

        gridApi.setGridOption('loading', false);

        const pagination = await firstValueFrom(
          this.store.select(InvoicingDataState.getPagination),
        );
        const activities = await firstValueFrom(
          this.store.select(InvoicingDataState.activities),
        );

        if (activities?.length) {
          const copiedActivities = cloneDeep(activities);

          const sortedActivities = this.sortInvoiceActivities(
            copiedActivities,
            params.sortModel,
          );

          const shiftIds = [
            ...new Set(
              sortedActivities.map((activity) => activity.shiftDetails.shiftId),
            ),
          ];

          if (hasShiftAttachmentReportsReadAccess && shiftIds.length) {
            this.fetchShiftAttachmentReports(shiftIds);
          }

          params.successCallback(sortedActivities, pagination?.totalRecords);
          gridApi.hideOverlay();
        } else {
          params.successCallback([], 0);
          gridApi.showNoRowsOverlay();
        }
      },
    };

    // Skip the first emission to avoid loading data on default filters data
    this.store
      .select(InvoicingDataState.getFilters)
      .pipe(
        // Skip must happen before debounceTime to prevent filters from the url from being skipped. See
        // See bug report: https://menlo1679.atlassian.net/browse/WILP-10858
        skip(1),
        debounceTime(400),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        gridApi.setGridOption('datasource', dataSource);
        gridApi.deselectAll();
      });
  }

  sortInvoiceActivities(
    activities: ActivityViewModel[],
    sortModel: SortModelItem[],
  ): ActivityViewModel[] {
    const ascSort = sortModel[0]?.sort === 'asc';
    const colId = sortModel[0]?.colId;

    if (colId === 'user') {
      activities.sort((a, b) => {
        return a.user && b.user
          ? ascSort
            ? a.user.localeCompare(b.user)
            : b.user.localeCompare(a.user)
          : 0;
      });
    }

    if (colId === 'shiftDetails.shiftName') {
      activities.sort((a, b) => {
        return a.shiftDetails.shiftName && b.shiftDetails.shiftName
          ? ascSort
            ? a.shiftDetails.shiftName.localeCompare(b.shiftDetails.shiftName)
            : b.shiftDetails.shiftName.localeCompare(a.shiftDetails.shiftName)
          : 0;
      });
    }

    if (colId === 'serviceDetails.serviceName') {
      activities.sort((a, b) => {
        const aServiceName = a.serviceDetails?.serviceName || '';
        const bServiceName = b.serviceDetails?.serviceName || '';

        return ascSort
          ? aServiceName.localeCompare(bServiceName)
          : bServiceName.localeCompare(aServiceName);
      });
    }

    if (colId === 'shiftDetails.shiftReportedStartDate') {
      activities.sort((a, b) => {
        const aShiftReportedStartDate =
          a.shiftDetails?.shiftReportedStartDate || new Date().toISOString();
        const bShiftReportedStartDate =
          b.shiftDetails?.shiftReportedStartDate || new Date().toISOString();

        const dateA = new Date(aShiftReportedStartDate);
        const dateB = new Date(bShiftReportedStartDate);

        return ascSort
          ? dateA.getTime() - dateB.getTime()
          : dateB.getTime() - dateA.getTime();
      });
    }

    if (colId === 'formattedStartDateTime') {
      const parseDate = (dateStr: string): Date => {
        const parsedDate = new Date(dateStr);
        return isValid(parsedDate) ? parsedDate : new Date();
      };

      activities.sort((a, b) => {
        const aFormattedStartDateTime =
          a.formattedStartDateTime || new Date().toISOString();
        const bFormattedStartDateTime =
          b.formattedStartDateTime || new Date().toISOString();

        const dateA = parseDate(aFormattedStartDateTime);
        const dateB = parseDate(bFormattedStartDateTime);

        return ascSort
          ? dateA.getTime() - dateB.getTime()
          : dateB.getTime() - dateA.getTime();
      });
    }

    return activities;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  private shiftDateFormatter(params: ValueFormatterParams): string {
    if (!params.value) return '---';
    return this.localizationDateFormat.toFormattedSignal(params.value)();
  }

  private fetchShiftAttachmentReports(shiftIds: string[]): void {
    this.shiftAttachmentReports$ = this.shiftAttachmentsService
      .getBulkReportAttachmentsByShiftIds(shiftIds)
      .pipe(shareReplay());
  }
}
